import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import styles from "./IrluxStatusSvg.module.css";

type Status = "up" | "warn" | "down";

export type IrluxStatusProps = {
  ping: {
    middleToForeign: number;
    middleToProton: number;
    clientToMiddleICMP: number;
  };
  status?: { middle?: Status; foreign?: Status; proton?: Status };
  className?: string;
  country?: string;
};

const colorBy = (s: Status = "up") =>
  s === "up" ? "#22c55e" : s === "warn" ? "#f59e0b" : "#ef4444";

/* ===== ثابت‌ها ===== */
const PULSE_SCALE = [1, 1.06, 1] as const;
const PULSE_TRANSITION = { repeat: Infinity, duration: 2 } as const;

const LED_PULSE = { opacity: [0.6, 1, 0.6] } as const;
const LED_TRANSITION = { repeat: Infinity, duration: 2 } as const;

const BASELINE_FADE = { opacity: 1 } as const;
const BASELINE_TRANSITION = { duration: 0.4 } as const;

/* ===== hook: delay پایدار برای هم‌فاز نگه‌داشتن انیمیشن بعد از remount ===== */
function useSteadyDelay(periodMs: number, offsetMs = 0) {
  const delayRef = useRef<string>();
  if (!delayRef.current) {
    const now = performance.now();
    const phase = (now + offsetMs) % periodMs;
    delayRef.current = `-${(phase / 1000).toFixed(3)}s`;
  }
  return delayRef.current!;
}

/* ===== عدد نرم (tween از مقدار قبلی به جدید) ===== */
function AnimatedNumber({
  value,
  digits = 0,
  ease = [0.22, 1, 0.36, 1],
  duration,
}: {
  value: number;
  digits?: number;
  ease?: any;
  duration?: number | ((delta: number) => number);
}) {
  const mv = useMotionValue(value);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const unsub = mv.on("change", (v) => {
      setDisplay(Number((v as number).toFixed(digits)));
    });
    return () => unsub();
  }, [digits, mv]);

  useEffect(() => {
    const from = mv.get() as number;
    const to = value;
    if (from === to) return;
    const d =
      typeof duration === "function"
        ? duration(Math.abs(to - from))
        : duration ?? Math.min(1.0, Math.max(0.25, Math.abs(to - from) * 0.015));
    const controls = animate(mv, to, { duration: d, ease });
    return () => controls.stop();
  }, [value, duration, ease, mv]);

  return <>{display}</>;
}

/* ===== path انیمیشنی با delay پایدار و بدون re-render ===== */
const StaticFlowPath = React.memo(
  function StaticFlowPath() {
    const delay = useSteadyDelay(3000); // با period انیمیشن هم‌فاز می‌شود
    return (
      <path
        className={styles.flowPath}
        style={{ animationDelay: delay }}
        d="M230 220 H1010"
      />
    );
  },
  () => true // هرگز re-render نشه
);

const FireWall = React.memo(
  function StaticFlowPath() {
    
    return (<>
      <text className={styles.redNode} x="650" y="98">
فایروال ایران
    </text>
      <path
        className={styles.firewall}
        d="M611 300 V120"
      />
      </>
    );
  },
  () => true // هرگز re-render نشه
);

const GlobalInternetNode = React.memo(function GlobalInternetNode({
  color,
}: {
  color: string;
}) {
  return (
    <g transform="translate(1070,220)">
      <text className={styles.node} x="0" y="-70" textAnchor="middle">
        Global Internet
      </text>
      <text x="0" y="-50" className={styles.node2} textAnchor="middle">
        Public Network
      </text>

      {/* Globe container */}
      <g transform="translate(0,0)">
        {/* Outer ring (pulsing) */}
        <motion.circle
          cx="0"
          cy="0"
          r="34"
          fill="#152338"
          stroke={color}
          strokeWidth="2.5"
          initial={false}
          animate={{ scale: PULSE_SCALE, stroke: color }}
          transition={{ ...PULSE_TRANSITION, stroke: { duration: 0.2 } }}
        />

        {/* Globe fill */}
        <circle cx="0" cy="0" r="26" fill="#0b1a2e" stroke={color} strokeWidth="2" opacity="0.95" />

        {/* Latitudes */}
        <path d="M-24 0 H24" stroke={color} strokeWidth="1.6" opacity="0.9" fill="none" />
        <path
          d="M-22 -9 C-10 -4, 10 -4, 22 -9"
          stroke={color}
          strokeWidth="1.4"
          opacity="0.65"
          fill="none"
        />
        <path
          d="M-22 9 C-10 4, 10 4, 22 9"
          stroke={color}
          strokeWidth="1.4"
          opacity="0.65"
          fill="none"
        />

        {/* Longitudes */}
        <path
          d="M0 -24 C-8 -16, -8 16, 0 24 C8 16, 8 -16, 0 -24"
          stroke={color}
          strokeWidth="1.4"
          opacity="0.75"
          fill="none"
        />
        <path
          d="M-9 -22 C-4 -10, -4 10, -9 22"
          stroke={color}
          strokeWidth="1.2"
          opacity="0.45"
          fill="none"
        />
        <path
          d="M9 -22 C4 -10, 4 10, 9 22"
          stroke={color}
          strokeWidth="1.2"
          opacity="0.45"
          fill="none"
        />

        {/* Small signal dots */}
        {[0, 0.25, 0.5].map((d, i) => (
          <motion.circle
            key={i}
            cx={-16 + i * 16}
            cy="50"
            r="3.5"
            fill={color}
            initial={false}
            animate={LED_PULSE}
            transition={{ ...LED_TRANSITION, delay: d }}
          />
        ))}
      </g>
    </g>
  );
});

function IrluxStatusSvgAnimatedInner({
  ping,
  status,
  className,
  country,
}: IrluxStatusProps) {
  const okMid = colorBy(status?.middle ?? "up");
  const okFor = colorBy(status?.foreign ?? "up");
  const okPro = colorBy(status?.proton ?? "up");

  const animMid = useMemo(() => ({ stroke: okMid, scale: PULSE_SCALE }), [okMid]);
  const animFor = useMemo(() => ({ stroke: okFor, scale: PULSE_SCALE }), [okFor]);
  const animPro = useMemo(() => ({ stroke: okPro, scale: PULSE_SCALE }), [okPro]);

  return (
    <svg className={className} viewBox="0 0 1200 400" role="img" aria-label="IRLUX status">
      {/* کارت */}
      <rect className={styles.card} x="40" y="40" rx="18" ry="18" width="1120" height="350" />
      <text className={styles.title} x="1100" y="98">
        {country}
      </text>

      {/* مسیر اصلی با انیمیشن CSS و delay پایدار */}
      <StaticFlowPath />
<FireWall />
      {/* چک‌پوینت‌ها */}
      {[
        { x: 300, anim: animMid, color: okMid, key: "mid" },
        { x: 610, anim: animFor, color: okFor, key: "for" },
        { x: 910, anim: animPro, color: okPro, key: "pro" },
      ].map(({ x, anim, color, key }) => (
        <g key={key} transform={`translate(${x},220)`}>
          <motion.circle
            r="16"
            fill="#152338"
            stroke={color}
            strokeWidth="2.5"
            initial={false}
            animate={anim}
            transition={{ ...PULSE_TRANSITION, stroke: { duration: 0.2 } }}
          />
          <motion.path
            d="M -7 0 l4 6 l10 -10"
            fill="none"
            stroke={color}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={false}
            style={{ pathLength: 1 }}
            animate={{ stroke: color }}
            transition={{ duration: 0.2 }}
          />
        </g>
      ))}

      {/* برچسب‌های پینگ */}
      <text x="610" y="190" textAnchor="middle" className={styles.txt}>
        <AnimatedNumber value={ping.middleToForeign} /> ms
      </text>
      {/* <text x="910" y="190" textAnchor="middle" className={styles.txt}>
        <AnimatedNumber value={ping.middleToProton} /> ms
      </text> */}

      {/* baseline */}
      <motion.path
        d="M470 320 H1070"
        fill="none"
        stroke="#8ab4f8"
        strokeWidth="3"
        initial={false}
        animate={BASELINE_FADE}
        transition={BASELINE_TRANSITION}
      />
      <text x="770" y="310" textAnchor="middle" className={styles.txt}>
        <AnimatedNumber value={ping.middleToProton} /> ms
      </text>
      <text x="770" y="350" textAnchor="middle" className={styles.desc}>
      سرور میانی(ایران)
به اینترنت جهانی
      </text>

      {/* Clients */}
      <g transform="translate(170,220)">
        <circle cx="0" cy="-18" r="20" stroke="#e2e8f0" strokeWidth="3" fill="none" />
        <path d="M -35 26 q35 -32 70 0" stroke="#e2e8f0" strokeWidth="3" fill="none" strokeLinecap="round" />
        <text className={styles.node} x="0" y="-60" textAnchor="middle">
          کاربر
        </text>
      </g>

      {/* Middle */}
      <g transform="translate(470,220)">
        <text className={styles.node} x="0" y="-70" textAnchor="middle">
          سرور میانی(ایران)
        </text>
        <g transform="translate(-38,-36)">
          <rect className={styles.chip} x="0" y="0" rx="6" ry="6" width="76" height="24" />
          <rect className={styles.chip} x="0" y="28" rx="6" ry="6" width="76" height="24" />
          <rect className={styles.chip} x="0" y="56" rx="6" ry="6" width="76" height="24" />
          <motion.circle
            cx="12"
            cy="12"
            r="4"
            fill={okMid}
            initial={false}
            animate={LED_PULSE}
            transition={LED_TRANSITION}
          />
          <circle cx="24" cy="12" r="4" className={styles.ledOff} />
          <circle cx="36" cy="12" r="4" className={styles.ledOff} />
        </g>
      </g>

      {/* Foreign */}
      <g transform="translate(770,220)">
        <text className={styles.node} x="0" y="-70" textAnchor="middle">
          سرور خارجی
        </text>
        <g transform="translate(-38,-36)">
          <rect className={styles.chip} x="0" y="0" rx="6" ry="6" width="76" height="24" />
          <rect className={styles.chip} x="0" y="28" rx="6" ry="6" width="76" height="24" />
          <rect className={styles.chip} x="0" y="56" rx="6" ry="6" width="76" height="24" />
          <motion.circle
            cx="12"
            cy="12"
            r="4"
            fill={okFor}
            initial={false}
            animate={LED_PULSE}
            transition={{ ...LED_TRANSITION, delay: 0.3 }}
          />
          <motion.circle
            cx="24"
            cy="12"
            r="4"
            fill={okFor}
            initial={false}
            animate={LED_PULSE}
            transition={{ ...LED_TRANSITION, delay: 0.6 }}
          />
          <circle cx="36" cy="12" r="4" className={styles.ledOff} />
        </g>
      </g>

      {/* Proton */}
<GlobalInternetNode color={okPro} />
    </svg>
  );
}

/* memo: فقط وقتی واقعاً مقادیر تغییر کنند رندر */
function propsEqual(prev: IrluxStatusProps, next: IrluxStatusProps) {
  return (
    prev.country === next.country &&
    (prev.status?.middle ?? "up") === (next.status?.middle ?? "up") &&
    (prev.status?.foreign ?? "up") === (next.status?.foreign ?? "up") &&
    (prev.status?.proton ?? "up") === (next.status?.proton ?? "up") &&
    prev.ping.middleToForeign === next.ping.middleToForeign &&
    prev.ping.middleToProton === next.ping.middleToProton &&
    prev.ping.clientToMiddleICMP === next.ping.clientToMiddleICMP &&
    prev.className === next.className
  );
}

export default React.memo(IrluxStatusSvgAnimatedInner, propsEqual);